import React, { useCallback, useEffect } from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { FormValues } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import usePeopleData from 'hooks/usePeopleData';
import useCompanies from 'hooks/useCompanies';
import { useDataRefresh } from 'screens/Applications&WorkOrders/StepsLayout/Components/DetailsStep/hooks/useDataRefresh';
import ApplicatorRow from './components/ApplicatorRow';

const ApplicatorsSection: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const { people, loading: peopleLoading, loadPeople } = usePeopleData();
  const {
    companies,
    loading: companiesLoading,
    loadCompanies
  } = useCompanies();
  const { subscribe, getTrigger } = useDataRefresh();

  // listen for refresh triggers from other components
  useEffect(() => {
    const unsubscribe = subscribe(() => {
      if (getTrigger('create-application-people') > 0) {
        loadPeople();
      }
      if (getTrigger('create-application-companies') > 0) {
        loadCompanies();
      }
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe, getTrigger, loadPeople, loadCompanies]);

  const handlePersonChange = useCallback(
    (index: number, value: string) => {
      loadPeople();
      setFieldValue(`applicators.${index}.personId`, value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setFieldValue]
  );

  const handleCompanyChange = useCallback(
    (index: number, value: string) => {
      loadCompanies();
      setFieldValue(`applicators.${index}.companyId`, value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setFieldValue]
  );

  const handleRemoveApplicator = useCallback(
    (index: number, arrayHelpers: any) => {
      // Determine which applicator to focus after removal
      const focusIndex = index === 0 ? 0 : index - 1;

      if (values.applicators.length === 1) {
        setFieldValue(`applicators.${index}.personId`, '');
        setFieldValue(`applicators.${index}.companyId`, '');
      } else {
        arrayHelpers.remove(index);
      }

      // I've used setTimeout to make sure the focus will happen at end of call stack
      setTimeout(() => {
        const applicatorRow = document.getElementById(
          `applicator-row-${focusIndex}`
        );

        if (applicatorRow) {
          const [applicatorInput] = applicatorRow.querySelectorAll('input');
          if (applicatorInput && typeof applicatorInput.focus === 'function') {
            applicatorInput.focus();
          }
        }
      }, 0);
    },
    [values.applicators.length, setFieldValue]
  );

  return (
    <FieldArray
      name="applicators"
      render={arrayHelpers => (
        <div className="flex flex-col gap-4">
          {values.applicators.map((applicator, index) => (
            <div
              id={`applicator-row-${index}`}
              key={`applicator-row-${globalThis.crypto.randomUUID()}`}
            >
              <ApplicatorRow
                applicator={applicator}
                index={index}
                onPersonChange={value => handlePersonChange(index, value)}
                onCompanyChange={value => handleCompanyChange(index, value)}
                onRemove={() => handleRemoveApplicator(index, arrayHelpers)}
                people={people}
                companies={companies}
                peopleLoading={peopleLoading}
                companiesLoading={companiesLoading}
                loadPeople={loadPeople}
                loadCompanies={loadCompanies}
              />
            </div>
          ))}
        </div>
      )}
    />
  );
};

export default ApplicatorsSection;
